import { useStore } from '@store/index';
import React, { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { NotificationBell } from '@/components/mailbox/NotificationBell';
import { SentryFeedbackButton } from '@/components/SentryFeedbackButton';
import { WorkflowType } from '@/constants/dispositions';
import { useOrganizationSwitcher } from '@/hooks/useOrganizationSwitcher';
import { useProgramSwitcher } from '@/hooks/useProgramSwitcher';
import { useWorkflowSwitcher } from '@/hooks/useWorkflowSwitcher';
import { isUserVisibleWorkflow } from '@/utils/workflow';

import { OrganizationCombobox } from './OrganizationCombobox';
import { ProgramCombobox } from './ProgramCombobox';
import { TimeZoneSelector } from './TimeZoneSelector';
import { WorkflowCombobox } from './WorkflowCombobox';

/**
 * Header component - Always rendered at the top.
 * Shows: Logo, Organization Switcher, Program Switcher, base breadcrumb, and portal slots for additional breadcrumbs and actions.
 */
export function Header() {
  const {
    selectedOrganizationId,
    selectedProgramId,
    selectedWorkflowId,
    getOrganizationsArray,
    getProgramsArray,
    getWorkflowsArray,
  } = useStore();

  const location = useLocation();
  const { switchToOrganization } = useOrganizationSwitcher();
  const { switchToProgram } = useProgramSwitcher();
  const { switchToWorkflow } = useWorkflowSwitcher();

  const organizations = getOrganizationsArray();
  const programs = getProgramsArray();
  const workflows = getWorkflowsArray();

  const isQcRoute =
    matchPath({ path: '/:orgId/qc/*', end: false }, location.pathname) != null ||
    matchPath({ path: '/:orgId/qc', end: true }, location.pathname) != null;

  // Workforce Console is org-scoped (it has its own program filter and spans
  // every team the manager owns), so the program + workflow switchers in the
  // header are misleading there — hide them just like we do for QC.
  const isWorkforceRoute =
    matchPath({ path: '/:orgId/workforce', end: true }, location.pathname) != null;

  // My Cases is org-scoped (lists assigned cases across all programs), so hide
  // the program/workflow switchers — the breadcrumb collapses to "Org / My Cases".
  const isMyCasesRoute =
    matchPath({ path: '/:orgId/my-cases', end: true }, location.pathname) != null;

  // My Tasks is org-scoped for the same reason as My Cases.
  const isMyTasksRoute =
    matchPath({ path: '/:orgId/my-tasks', end: true }, location.pathname) != null;

  // All Tasks is org-scoped too: every assignee's tasks across the org's programs.
  const isAllTasksRoute =
    matchPath({ path: '/:orgId/all-tasks', end: true }, location.pathname) != null;

  // Analytics is org-scoped (the page has its own program multi-select filter
  // inside the view), so the program/workflow switchers in the header are
  // misleading — match QC's behavior.
  const isAnalyticsRoute =
    matchPath({ path: '/:orgId/analytics/*', end: false }, location.pathname) != null ||
    matchPath({ path: '/:orgId/analytics', end: true }, location.pathname) != null;

  // AE / PQC Analytics is org-scoped like Analytics (its own program filter).
  const isCasesAnalyticsRoute =
    matchPath({ path: '/:orgId/cases-analytics', end: true }, location.pathname) != null;

  // Org-scoped cases (Tier-2) lives at /org-{id}/cases (no :progId) and spans
  // every program in the org, so the program + workflow switchers are misleading
  // here — hide them like the other org-scoped routes. The program-scoped
  // /org/prog/cases route is unaffected (it carries :progId).
  const isOrgCasesRoute =
    matchPath({ path: '/:orgId/cases', end: true }, location.pathname) != null;

  // Webhooks is org-scoped (endpoints and deliveries span every program in the
  // org), so the program/workflow switchers are misleading — match QC.
  const isWebhooksRoute =
    matchPath({ path: '/:orgId/webhooks/*', end: false }, location.pathname) != null ||
    matchPath({ path: '/:orgId/webhooks', end: true }, location.pathname) != null;

  const isOrgScopedRoute =
    isQcRoute ||
    isWorkforceRoute ||
    isMyCasesRoute ||
    isMyTasksRoute ||
    isAllTasksRoute ||
    isAnalyticsRoute ||
    isCasesAnalyticsRoute ||
    isOrgCasesRoute ||
    isWebhooksRoute;

  // On detail pages (e.g. /cases/:id, /campaigns/:id), show workflow as
  // plain text since the workflow is determined by the record being viewed.
  const isDetailPage =
    matchPath(':orgId/:progId/cases/:caseId/*', location.pathname) != null ||
    matchPath(':orgId/:progId/campaigns/:campaignId/*', location.pathname) != null;
  const selectedWorkflow = workflows.find(w => w.id === selectedWorkflowId);

  // Only show workflow switcher for user-visible (non-internal) workflows
  const configuredWorkflows = useMemo(() => workflows.filter(isUserVisibleWorkflow), [workflows]);

  return (
    <div className="border-b px-3.5 h-[50px]">
      <div className="flex items-center justify-between h-full">
        <div className="flex items-center gap-[2px]">
          <div className="shrink-0 mr-1">
            <img src="/images/company/hms-header-logo.png" alt="100ms logo" className="h-5 w-5" />
          </div>
          <div className="text-border">/</div>

          {/* Organization Switcher */}
          {organizations.length < 2 ? (
            <div className="text-sm px-1">
              {organizations[0]?.name || 'No organizations available'}
            </div>
          ) : (
            <OrganizationCombobox
              organizations={organizations}
              selectedOrganizationId={selectedOrganizationId}
              onOrganizationChange={switchToOrganization}
              disabled={organizations.length === 0}
            />
          )}

          {!isOrgScopedRoute && (
            <>
              {/* Program Switcher */}
              <div className="text-border">/</div>
              {programs.length < 2 ? (
                <div className="text-sm px-1">{programs[0]?.name || 'No programs available'}</div>
              ) : (
                <ProgramCombobox
                  programs={programs}
                  selectedProgramId={selectedProgramId}
                  onProgramChange={switchToProgram}
                  disabled={programs.length === 0}
                />
              )}

              <div className="text-border">/</div>

              {/* Workflow Switcher — show when multiple workflows configured, or when PQC selector flag is on; read-only on detail pages */}
              {(configuredWorkflows.length > 1 ||
                configuredWorkflows.some(w => w.workflow_type === WorkflowType.PQC)) && (
                <>
                  {isDetailPage ? (
                    <div className="text-sm px-1">{selectedWorkflow?.name || ''}</div>
                  ) : (
                    <WorkflowCombobox
                      workflows={configuredWorkflows}
                      selectedWorkflowId={selectedWorkflowId}
                      onWorkflowChange={switchToWorkflow}
                      showForPQC
                    />
                  )}
                  <div className="text-border">/</div>
                </>
              )}
            </>
          )}

          {isOrgScopedRoute && <div className="text-border">/</div>}

          {/* Breadcrumb portal slot - pages render breadcrumbs here */}
          <div id="breadcrumb-portal-root" className="flex items-center" />
        </div>

        {/* Header actions portal slot */}
        <div className="flex items-center gap-3 mr-2.5">
          <NotificationBell />
          <TimeZoneSelector />
          <SentryFeedbackButton />
          <div id="header-actions-portal-root" className="flex items-center gap-3 empty:hidden" />
        </div>
      </div>
    </div>
  );
}
